import type React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Button,
    Description,
    FieldError,
    Input,
    Label,
    TextField,
    Typography,
} from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { RowDrawerTypedBarcodeStatus } from './rowDrawerResolutions';

type RowDrawerTypedBarcodeProps = {
    value: string;
    status: RowDrawerTypedBarcodeStatus;
    onChange: (value: string) => void;
    onUseAnyway: () => void;
};

/**
 * The barcode the owner types for the variant (rule 7, AC-05): 1 to 14
 * digits. Letters or more digits are refused inline as not a number; an 8-
 * or 13-digit code whose check digit doesn't match shows the warning with
 * **Use it anyway**, which accepts that code ("Used as typed."); any other
 * length is not checked. The copy is the shared `components.barcodeEntry`.
 */
export const RowDrawerTypedBarcode: React.FC<RowDrawerTypedBarcodeProps> = ({
    value,
    status,
    onChange,
    onUseAnyway,
}) => {
    const { t } = useTranslation('components');

    return (
        <div className="flex flex-col gap-2">
            <TextField
                className="flex w-full flex-col gap-1"
                isInvalid={status === 'notDigits'}
                onChange={onChange}
                value={value}
                {...getNodeTestIdProp(
                    'catalog-import-row-drawer-typed-barcode',
                )}
            >
                <Label>{t('barcodeEntry.label')}</Label>
                <Input
                    inputMode="numeric"
                    placeholder={t('barcodeEntry.placeholder')}
                />
                <Description>{t('barcodeEntry.help')}</Description>
                <FieldError>{t('barcodeEntry.notDigits')}</FieldError>
            </TextField>
            {status === 'checkDigit' ? (
                <Alert
                    status="warning"
                    {...getNodeTestIdProp(
                        'catalog-import-row-drawer-typed-barcode-check-digit',
                    )}
                >
                    <Alert.Indicator />
                    <Alert.Content>
                        <Alert.Description>
                            {t('barcodeEntry.checkDigit')}
                        </Alert.Description>
                    </Alert.Content>
                    <Button onPress={onUseAnyway} size="sm" variant="tertiary">
                        {t('barcodeEntry.useAnyway')}
                    </Button>
                </Alert>
            ) : null}
            {status === 'usedAnyway' ? (
                <Typography color="muted" type="body-xs">
                    {t('barcodeEntry.usedAnyway')}
                </Typography>
            ) : null}
        </div>
    );
};
